import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Card, Chip, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';

import invoicesApi from '../../../api/invoicesApi';
import ordersApi from '../../../api/ordersApi';
import { getErrorMessage, getBlobErrorMessage } from '../../../utils/errorHandler';
import { printBlob, triggerBlobDownload } from '../../../utils/downloadFile';
import { INVOICE_STATUS_META, isAcceptedInvoiceStatus } from '../../../utils/invoiceStatus';
import OrderDetailDialog from '../../../sections/admin/orders/OrderDetailDialog';
import VoidInvoiceDialog from '../../../sections/admin/orders/VoidInvoiceDialog';

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency: currency || 'PEN' }).format(
    value || 0
  );

export default function InvoicesListPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [orderStatuses, setOrderStatuses] = useState([]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Numero de serie de la unidad fisica vendida (ver ProductSerial), no la serie del
  // comprobante: sirve para ubicar el comprobante de un producto puntual por su serie/IMEI.
  const [serieInput, setSerieInput] = useState('');
  const [serieFilter, setSerieFilter] = useState('');
  const [issueDateFrom, setIssueDateFrom] = useState('');
  const [issueDateTo, setIssueDateTo] = useState('');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrderId, setLoadingOrderId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [invoiceToVoid, setInvoiceToVoid] = useState(null);
  const [voiding, setVoiding] = useState(false);

  // Debounce del buscador para no disparar una peticion en cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSerieFilter(serieInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timeout);
  }, [serieInput]);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const { data } = await ordersApi.getOrderStatuses();
        setOrderStatuses(data.data);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar los estados.'), { variant: 'error' });
      }
    };

    loadStatuses();
  }, [enqueueSnackbar]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await invoicesApi.getInvoices({
        search: search || undefined,
        serie: serieFilter || undefined,
        issue_date_from: issueDateFrom || undefined,
        issue_date_to: issueDateTo || undefined,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      });
      setRows(data.data);
      setRowCount(data.meta.total);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar el historial de comprobantes.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, serieFilter, issueDateFrom, issueDateTo, paginationModel, enqueueSnackbar]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleViewOrder = async (invoice) => {
    setLoadingOrderId(invoice.id);
    try {
      const { data } = await ordersApi.getOrder(invoice.order.id);
      setSelectedOrder(data.data);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar el pedido.'), { variant: 'error' });
    } finally {
      setLoadingOrderId(null);
    }
  };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      const response = await ordersApi.downloadInvoicePdf(invoice.order.id);
      triggerBlobDownload(response.data, `${invoice.type}-${invoice.full_number}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo descargar el comprobante.'), { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  // Pide el sustento (Catalogo 09 de SUNAT) y una observacion en VoidInvoiceDialog antes de
  // anular; el backend envia la nota de credito electronica a SUNAT (ver ordersApi.voidInvoice).
  const handleConfirmVoid = async (reasonCode, observation) => {
    if (!invoiceToVoid) {
      return;
    }
    setVoiding(true);
    try {
      const { data } = await ordersApi.voidInvoice(invoiceToVoid.order.id, reasonCode, observation);
      enqueueSnackbar(data.message || 'Comprobante anulado correctamente.', { variant: 'success' });
      setInvoiceToVoid(null);
      await fetchInvoices();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo anular el comprobante.'), { variant: 'error' });
    } finally {
      setVoiding(false);
    }
  };

  const handleDownloadCreditNote = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      const response = await ordersApi.downloadCreditNotePdf(invoice.order.id);
      triggerBlobDownload(response.data, `nota-credito-${invoice.credit_note?.full_number || `pedido-${invoice.order.id}`}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo generar la nota de crédito.'), { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  // Comprobante o nota de credito en formato ticket de 80mm, directo a la tiketera.
  const handlePrintTicket = async (invoice, request, fallbackMessage) => {
    setDownloadingId(invoice.id);
    try {
      const response = await request();
      printBlob(response.data);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, fallbackMessage), { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOrderChanged = async () => {
    await fetchInvoices();
  };

  const columns = useMemo(
    () => [
      {
        field: 'full_number',
        headerName: 'Comprobante',
        width: 190,
        renderCell: (params) => (
          <Chip label={`${params.row.type === 'factura' ? 'Factura' : 'Boleta'} ${params.value}`} size="small" />
        ),
      },
      {
        field: 'client',
        headerName: 'Cliente',
        flex: 1,
        minWidth: 160,
        valueGetter: (value, row) => row.order?.client?.name || '—',
      },
      {
        field: 'email',
        headerName: 'Correo',
        flex: 1,
        minWidth: 200,
        valueGetter: (value, row) => row.order?.client?.email || '—',
      },
      {
        field: 'total',
        headerName: 'Total',
        width: 130,
        valueGetter: (value, row) => formatMoney(row.total, row.order?.currency),
      },
      {
        field: 'status',
        headerName: 'Estado SUNAT',
        width: 190,
        renderCell: (params) => (
          <Chip
            label={INVOICE_STATUS_META[params.value]?.label || params.value}
            color={INVOICE_STATUS_META[params.value]?.color || 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'issue_date',
        headerName: 'Fecha de emisión',
        width: 140,
        valueFormatter: (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('es-PE') : '—'),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 170,
        getActions: (params) => {
          const actions = [
            <GridActionsCellItem
              key="view"
              icon={<VisibilityIcon />}
              label="Ver pedido"
              onClick={() => handleViewOrder(params.row)}
              disabled={loadingOrderId === params.row.id}
            />,
          ];

          if (isAcceptedInvoiceStatus(params.row.status)) {
            actions.push(
              <GridActionsCellItem
                key="download"
                icon={<DownloadIcon />}
                label="Descargar PDF"
                onClick={() => handleDownload(params.row)}
                disabled={downloadingId === params.row.id}
              />,
              <GridActionsCellItem
                key="print"
                icon={<PrintIcon />}
                label="Imprimir ticket"
                onClick={() =>
                  handlePrintTicket(
                    params.row,
                    () => ordersApi.downloadInvoicePdf(params.row.order.id, 'ticket'),
                    'No se pudo imprimir el comprobante.'
                  )
                }
                disabled={downloadingId === params.row.id}
              />
            );
          }

          if (params.row.status !== 'anulada') {
            actions.push(
              <GridActionsCellItem
                key="void"
                icon={<BlockIcon />}
                label="Anular"
                onClick={() => setInvoiceToVoid(params.row)}
              />
            );
          } else if (params.row.void_reason_code) {
            actions.push(
              <GridActionsCellItem
                key="credit-note"
                icon={<ReceiptIcon />}
                label="Nota de crédito"
                onClick={() => handleDownloadCreditNote(params.row)}
                disabled={downloadingId === params.row.id}
              />,
              <GridActionsCellItem
                key="print-credit-note"
                icon={<PrintIcon />}
                label="Imprimir nota de crédito"
                onClick={() =>
                  handlePrintTicket(
                    params.row,
                    () => ordersApi.downloadCreditNotePdf(params.row.order.id, 'ticket'),
                    'No se pudo imprimir la nota de crédito.'
                  )
                }
                disabled={downloadingId === params.row.id}
              />
            );
          }

          return actions;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingOrderId, downloadingId]
  );

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4">Boletas y facturas</Typography>
        <Typography variant="body2" color="text.secondary">
          Historial de comprobantes electrónicos (SUNAT) emitidos para los pedidos de la tienda.
        </Typography>
      </div>

      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Buscar"
              placeholder="N.° de comprobante, nombre o correo del cliente"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="N.° de serie"
              placeholder="Serie del producto"
              value={serieInput}
              onChange={(e) => setSerieInput(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              fullWidth
              type="date"
              label="Emitido desde"
              value={issueDateFrom}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setIssueDateFrom(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField
              fullWidth
              type="date"
              label="Emitido hasta"
              value={issueDateTo}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setIssueDateTo(e.target.value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            />
          </Grid>
        </Grid>
      </Card>

      <Card>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
        />
      </Card>

      <OrderDetailDialog
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
        statuses={orderStatuses}
        onChanged={handleOrderChanged}
      />

      <VoidInvoiceDialog
        open={Boolean(invoiceToVoid)}
        invoice={invoiceToVoid}
        voiding={voiding}
        onClose={() => setInvoiceToVoid(null)}
        onConfirm={handleConfirmVoid}
      />
    </Stack>
  );
}
